<template>
  <div v-if="isLoadingAuth" class="flex items-center justify-center h-screen">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>
  <div v-else-if="loading" class="flex items-center justify-center h-screen">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>
  <div v-else-if="!user" class="text-red-500 text-center p-4">
    User not logged in
  </div>
  <div v-else-if="errorMessage" class="text-red-300 text-center p-4">
    {{ errorMessage }}
  </div>
  <div v-else class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-70 transition-opacity duration-300"></div>
    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-md">
          <div class="p-5">
            <!-- Header -->
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-2xl font-semibold text-red-500" id="modal-title">คุณตายแล้ว! {{ reason }}</h2>
            </div>

            <!-- Tabs -->
            <div class="flex border-b border-gray-600 mb-4">
              <button
                class="flex-1 py-2 text-center text-sm font-medium"
                :class="activeTab === 'punishment' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-400 hover:text-indigo-300'"
                @click="activeTab = 'punishment'"
                v-tooltip="'ดูบทลงโทษ'"
              >
                บทลงโทษ
              </button>
              <button
                v-if="eventData.killedBySurvivor && eventData.reason !== 'ถูกหุ่นยนต์ฆ่า'"
                class="flex-1 py-2 text-center text-sm font-medium"
                :class="activeTab === 'killer' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-400 hover:text-indigo-300'"
                @click="activeTab = 'killer'"
                v-tooltip="'ดูข้อมูลผู้ฆ่า'"
              >
                ผู้ฆ่า
              </button>
            </div>

            <!-- Punishment Tab -->
            <div v-if="activeTab === 'punishment'" class="space-y-4">
              <p v-if="!eventData.punishment" class="text-indigo-200 text-center text-sm">
                คุณจะต้องสูญเสียสิ่งต่อไปนี้เมื่อยอมรับบทลงโทษ
              </p>

              <div v-if="loading" class="flex flex-col items-center justify-center gap-2">
                <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
                <p class="text-indigo-200 text-xs">กำลังโหลดข้อมูล...</p>
              </div>
              <div v-else class="space-y-4 max-h-64 overflow-y-auto pr-2">
                <!-- Insurance Message (Before Accepting) -->
                <div v-if="insuranceActive && !eventData.punishment" class="bg-green-700 rounded-md p-3 border border-green-600">
                  <p class="text-sm text-green-200 font-medium text-center">ยินดีด้วย คุณมีประกัน!</p>
                  <p class="text-green-300 text-xs mt-2 text-center">คุณจะไม่สูญเสีย: ไอเทมที่สวมใส่, ของในกระเป๋า, ลายสัก, ทักษะ</p>
                  <p class="text-green-300 text-xs text-center">แต่จะสูญเสีย: ห้อง เพชร/ทอง/เหรียญ ตามปกติ</p>
                  <p class="text-yellow-300 text-xs text-center mt-2">ประกันหมดอายุใน: {{ formatRemainingTime(insuranceRemaining) }}</p>
                </div>

                <!-- Items to Lose (Before Accepting Punishment) -->
                <div v-if="!eventData.punishment" class="bg-gray-700 rounded-md p-3 border border-gray-600">
                  <p class="text-sm text-indigo-200 font-medium">สิ่งที่จะสูญเสีย</p>
                  <ul class="text-indigo-200 text-xs space-y-2 mt-2">
                    <li>ไอเทมที่สวมใส่: <span class="font-semibold">{{ eventData.itemsToLoseCount || 0 }}</span></li>
                    <li>ทักษะ: <span class="font-semibold">{{ eventData.skillsToLoseCount || 0 }}</span></li>
                    <li>ลายสัก: <span class="font-semibold">{{ eventData.tattoosToLoseCount || 0 }}</span></li>
                    <li>ห้อง: <span class="font-semibold">{{ eventData.roomsToLoseCount || 0 }}</span></li>
                    <li v-if="eventData.roomsToLoseCount === 0 && (characterData.room || 0) <= 1" class="text-gray-400">
                      (ไม่เสียห้อง เนื่องจากเหลือเพียง 1 ห้อง)
                    </li>
                    <li v-if="eventData.roomsToLoseCount === 1 && (characterData.room || 0) <= 1" class="text-gray-400">
                      (ไม่เสียห้อง เนื่องจากเหลือเพียง 1 ห้อง)
                    </li>
                    <li>ของในกระเป๋า: <span class="font-semibold">{{ eventData.bagItemsToLoseCount || 0 }}</span></li>
                  </ul>
                </div>

                <!-- Lost Items (After Accepting Punishment) -->
                <div v-if="eventData.punishment" class="space-y-1">
                  <!-- Insurance Message (After Accepting) -->
                  <div v-if="insuranceActive" class="bg-green-700 rounded-md p-3 border border-green-600 mb-4">
                    <p class="text-sm text-green-200 font-medium text-center">ยินดีด้วย คุณมีประกัน!</p>
                    <p class="text-green-300 text-xs mt-2 text-center">คุณจะไม่สูญเสีย: ไอเทมที่สวมใส่, ของในกระเป๋า, ลายสัก, ทักษะ เมื่อเกิดใหม่</p>
                    <p class="text-green-300 text-xs text-center">แต่จะสูญเสีย: ห้อง , เพชร/ทอง/เหรียญ ตามปกติ</p>
                    <p class="text-yellow-300 text-xs text-center mt-2">ประกันหมดอายุใน: {{ formatRemainingTime(insuranceRemaining) }}</p>
                  </div>

                  <!-- Lost Skills -->
                  <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                    <p class="text-sm text-indigo-200 font-medium text-center">ทักษะที่สูญเสีย</p>
                    <div v-if="skillsToLose.length > 0" class="grid grid-cols-3 gap-2 mt-2">
                      <div
                        v-for="skill in skillsToLose"
                        :key="skill.name"
                        class="p-2 rounded bg-gray-900 text-indigo-300 flex flex-col items-center space-y-1 transition-all duration-200 hover:bg-gray-800"
                      >
                        <img
                          :src="skill.img"
                          @error="handleImageError($event)"
                          class="w-10 h-10 rounded object-cover"
                        />
                        <p class="text-xs text-center">{{ skill.name }}</p>
                      </div>
                    </div>
                    <p v-if="insuranceActive && skillsToLose.length > 0" class="text-green-400 text-xs text-center mt-2">แต่เพราะมีประกัน คุณจะไม่สูญเสียจริงๆ</p>
                    <p v-else class="text-gray-400 text-xs text-center mt-2">ไม่มีทักษะที่สูญเสีย</p>
                  </div>

                  <!-- Lost Tattoos -->
                  <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                    <p class="text-sm text-indigo-200 font-medium text-center">ลายสักที่สูญเสีย</p>
                    <div v-if="tattoosToLose.length > 0" class="grid grid-cols-3 gap-2 mt-2">
                      <div
                        v-for="tattoo in tattoosToLose"
                        :key="tattoo.name"
                        class="p-2 rounded bg-gray-900 text-indigo-300 flex flex-col items-center space-y-1 transition-all duration-200 hover:bg-gray-800"
                      >
                        <img
                          :src="tattoo.imageUrl"
                          @error="handleImageError($event)"
                          class="w-10 h-10 rounded object-cover"
                        />
                        <p class="text-xs text-center">{{ tattoo.name }}</p>
                      </div>
                    </div>
                    <p v-if="insuranceActive && tattoosToLose.length > 0" class="text-green-400 text-xs text-center mt-2">แต่เพราะมีประกัน คุณจะไม่สูญเสียจริงๆ</p>
                    <p v-else class="text-gray-400 text-xs text-center mt-2">ไม่มีลายสักที่สูญเสีย</p>
                  </div>

                  <!-- Lost Items -->
                  <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                    <p class="text-sm text-indigo-200 font-medium text-center">ไอเทมที่สูญเสีย</p>
                    <div v-if="itemsToLose.length > 0" class="grid grid-cols-3 gap-2 mt-2">
                      <div
                        v-for="item in itemsToLose"
                        :key="item.id"
                        class="p-2 rounded bg-gray-900 flex flex-col items-center space-y-1 transition-all duration-200 hover:bg-gray-800"
                      >
                        <div class="relative">
                          <img
                            :src="item.img"
                            @error="handleImageError($event)"
                            class="w-12 h-12 rounded-md object-cover border-2"
                            :class="[item.rarityColors?.borderColor || 'border-gray-600', item.rarityColors?.bgColor || 'bg-gray-800']"
                          />
                          <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                            <span class="text-xs font-semibold">{{ item.rarity || 0 }}</span>
                            <i class="fas fa-star text-yellow-400 text-xs"></i>
                          </div>
                        </div>
                        <p
                          class="text-xs text-center"
                          :class="item.rarityColors?.textColor || 'text-indigo-300'"
                        >
                          {{ item.name }}<span v-if="item.level">+{{ item.level }}</span>
                          <i v-if="item.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
                        </p>
                      </div>
                    </div>
                    <p v-if="insuranceActive && itemsToLose.length > 0" class="text-green-400 text-xs text-center mt-2">แต่เพราะมีประกัน คุณจะไม่สูญเสียจริงๆ</p>
                    <p v-else class="text-gray-400 text-xs text-center mt-2">ไม่มีไอเทมที่สูญเสีย</p>
                  </div>

                  <!-- Lost Bag Items -->
                  <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                    <p class="text-sm text-indigo-200 font-medium text-center">ของในกระเป๋าที่สูญเสีย</p>
                    <div v-if="bagItemsToLose.length > 0" class="grid grid-cols-3 gap-2 mt-2">
                      <div
                        v-for="item in bagItemsToLose"
                        :key="item.id"
                        class="p-2 rounded bg-gray-900 flex flex-col items-center space-y-1 transition-all duration-200 hover:bg-gray-800"
                      >
                        <div class="relative">
                          <img
                            :src="item.img"
                            @error="handleImageError($event)"
                            class="w-12 h-12 rounded-md object-cover border-2"
                            :class="[item.rarityColors?.borderColor || 'border-gray-600', item.rarityColors?.bgColor || 'bg-gray-800']"
                          />
                          <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                            <span class="text-xs font-semibold">{{ item.rarity || 0 }}</span>
                            <i class="fas fa-star text-yellow-400 text-xs"></i>
                          </div>
                        </div>
                        <p
                          class="text-xs text-center"
                          :class="item.rarityColors?.textColor || 'text-indigo-300'"
                        >
                          {{ item.name }}<span v-if="item.level">+{{ item.level }}</span>
                          <i v-if="item.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
                        </p>
                      </div>
                    </div>
                    <p v-if="insuranceActive && bagItemsToLose.length > 0" class="text-green-400 text-xs text-center mt-2">แต่เพราะมีประกัน คุณจะไม่สูญเสียจริงๆ</p>
                    <p v-else class="text-gray-400 text-xs text-center mt-2">ไม่มีของในกระเป๋าที่สูญเสีย</p>
                  </div>

                  <!-- Rooms -->
                  <div v-if="characterData.room > 1" class="bg-gray-700 rounded-md p-3 border border-gray-600">
                    <p class="text-sm text-indigo-200 font-medium text-center">ห้อง</p>
                    <div v-if="characterData.room > 0" class="flex items-center flex-wrap gap-2 mt-2">
                      <i
                        v-for="room in characterData.room"
                        :key="room"
                        class="fas fa-door-closed text-indigo-300 text-lg bg-gray-900 p-2 rounded"
                        :class="{ 'border-2 border-red-500': room === characterData.room && eventData.roomsToLoseCount > 0 && (characterData.room || 0) > 1 }"
                      ></i>
                    </div>
                    <p v-if="eventData.roomsToLoseCount > 0 && (characterData.room || 0) > 1" class="text-red-300 text-xs mt-2 text-center">
                      ห้องสุดท้ายจะถูกลดเมื่อเกิดใหม่
                    </p>
                  </div>
                </div>
              </div>

              <!-- Cooldown Display if waitRequired -->
              <div v-if="eventData.waitRequired && remainingTime > 0" class="bg-yellow-700 rounded-md p-3 border border-yellow-600 mt-4">
                <p class="text-sm text-yellow-200 font-medium text-center">คุณต้องรอเพื่อเกิดใหม่</p>
                <p class="text-yellow-300 text-lg font-semibold text-center mt-2">{{ formatRemainingTime(remainingTime) }}</p>
              </div>

              <!-- Urge to Reborn if Insurance Active (After Accepting) -->
              <div v-if="eventData.punishment && insuranceActive" class="bg-yellow-700 rounded-md p-3 border border-yellow-600 mt-4">
                <p class="text-sm text-yellow-200 font-medium text-center">รีบเกิดใหม่!</p>
                <p class="text-yellow-300 text-xs text-center mt-2">ก่อนประกันหมดอายุใน {{ formatRemainingTime(insuranceRemaining) }}</p>
              </div>

              <!-- Migrate Warning Banner -->
              <div v-if="needsMigrate" class="bg-red-900/80 border border-red-500 rounded-md p-3 mb-3">
                <p class="text-red-200 text-sm font-semibold text-center">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  คุณต้องย้ายระบบไอเทมใหม่ก่อน
                </p>
                <p class="text-red-300 text-xs text-center mt-1">
                  กรุณาปิดหน้าต่างนี้และทำการย้ายระบบไอเทมก่อนจึงจะสามารถยอมรับบทลงโทษหรือเกิดใหม่ได้
                </p>
              </div>

              <!-- Action Buttons -->
              <div class="space-y-2">
                <button
                  v-if="!eventData.punishment"
                  @click="handleAcceptPunishment"
                  :disabled="loading || needsMigrate"
                  class="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  v-tooltip="'ยอมรับบทลงโทษและสูญเสียไอเทม'"
                >
                  <span v-if="!loading">ยอมรับบทลงโทษ</span>
                  <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </button>
                <button
                  v-else
                  @click="handleBorn"
                  :disabled="loading || (eventData.waitRequired && remainingTime > 0) || needsMigrate"
                  class="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  v-tooltip="'เกิดใหม่และกลับสู่เกม'"
                >
                  <span v-if="!loading">เกิดใหม่</span>
                  <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </button>
              </div>
            </div>

            <!-- Killer Tab -->
            <div v-if="activeTab === 'killer' && eventData.killedBySurvivor" class="space-y-4">
              <p class="text-indigo-200 text-center text-sm">คุณถูกผู้เล่นอื่นฆ่า! นี่คือข้อมูลของผู้ฆ่าและทรัพยากรที่ถูกปล้น</p>
              <div v-if="loading" class="flex flex-col items-center justify-center gap-2">
                <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
                <p class="text-indigo-200 text-xs">กำลังโหลดข้อมูล...</p>
              </div>
              <div v-else class="space-y-4 max-h-64 overflow-y-auto pr-2">
                <!-- Killer Info -->
                <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                  <p class="text-sm text-indigo-200 font-medium">ผู้ฆ่า</p>
                  <p class="text-indigo-300 text-lg font-semibold mt-2">{{ killerData.characterName || 'ไม่ทราบชื่อ' }}</p>
                </div>

                <!-- Stolen Resources -->
                <div class="bg-gray-700 rounded-md p-3 border border-gray-600">
                  <p class="text-sm text-indigo-200 font-medium">ทรัพยากรที่ถูกปล้น</p>
                  <ul class="text-indigo-200 text-xs space-y-2 mt-2">
                    <li>เพชร: <span class="font-semibold">{{ eventData.diamondStolen || 0 }}</span></li>
                    <li>ทอง: <span class="font-semibold">{{ eventData.goldStolen || 0 }}</span></li>
                    <li>เหรียญ: <span class="font-semibold">{{ eventData.silverStolen || 0 }}</span></li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="errorMessage" class="mt-3 text-center text-red-300 text-xs">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  emits: ['bornCompleted'],
  directives: {
    tooltip: {
      beforeMount(el, binding) {
        el.setAttribute('data-tooltip', binding.value);
        el.classList.add('relative');
      },
    },
  },
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const loading = ref(true);
    const errorMessage = ref('');
    const eventData = ref({});
    const characterData = ref({});
    const characterPrivateData = ref({});
    const skillsToLose = ref([]);
    const tattoosToLose = ref([]);
    const itemsToLose = ref([]);
    const bagItemsToLose = ref([]);
    const killerData = ref({});
    const activeTab = ref('punishment');
    const reason = ref('');
    const currentTime = ref(Date.now());
    const needsMigrate = computed(() => !playerStore.userData?.migratedToItemV2);
    let unsubscribeEvent = null;
    let intervalId = null;

    const remainingTime = computed(() => {
      if (!eventData.value.waitRequired || !eventData.value.waitUntil) return 0;
      const waitUntilMillis = eventData.value.waitUntil.toMillis();
      return Math.max(0, waitUntilMillis - currentTime.value);
    });

    const insuranceRemaining = computed(() => {
      if (!characterPrivateData.value.insuranceExpiry) return 0;
      const expMillis = characterPrivateData.value.insuranceExpiry.toMillis();
      return Math.max(0, expMillis - currentTime.value);
    });

    const insuranceActive = computed(() => insuranceRemaining.value > 0);

    const formatRemainingTime = (millis) => {
      const days = Math.floor(millis / (1000 * 60 * 60 * 24));
      const hours = Math.floor((millis % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((millis % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((millis % (1000 * 60)) / 1000);
      return `${days}วัน ${hours}ชั่วโมง ${minutes}นาที ${seconds}วินาที`;
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };

    const defaultItemStats = {
      stamina: 0,
      str: 0,
      agi: 0,
      int: 0,
      maxHp: 0,
      atk: 0,
      critical: 0,
      evasion: 0,
      defense: 0,
      luck: 0,
    };

    const defaultRarityColors = {
      bgColor: 'bg-gray-700',
      textColor: 'text-gray-300',
      borderColor: 'border-gray-600',
    };

    const normalizeLostItemPreview = (instanceId, sourceItem = null, fallback = {}) => {
      const templateId = sourceItem?.templateId || fallback?.templateId || null;
      const template = templateId ? (gameItemsData[templateId] || {}) : {};
      const states = sourceItem?.states || fallback?.states || {};
      const resolvedId = instanceId || fallback?.instanceId || fallback?.id || templateId || 'unknown';
      const level = sourceItem?.states?.level ?? fallback?.level ?? states?.level ?? template?.level ?? null;
      const rarity = sourceItem?.states?.rarity ?? fallback?.rarity ?? states?.rarity ?? template?.rarity ?? 1;
      const rarityColors = fallback?.rarityColors || states?.rarityColors || template?.rarityColors || defaultRarityColors;
      return {
        id: resolvedId,
        instanceId: resolvedId,
        templateId,
        states,
        ...template,
        ...fallback,
        ...states,
        name: fallback?.name || states?.name || template?.name || resolvedId,
        img: fallback?.img || states?.img || template?.img || '../../assets/item/default.png',
        stats: fallback?.stats || states?.stats || template?.stats || defaultItemStats,
        rarityColors,
        rarity,
        level,
      };
    };

    const resolveLostItemPreviews = (instanceIds = [], sourceItems = {}, snapshots = []) => {
      const snapshotList = Array.isArray(snapshots) ? snapshots : [];
      const ids = Array.isArray(instanceIds) && instanceIds.length > 0
        ? instanceIds
        : snapshotList.map((entry) => entry?.instanceId || entry?.id).filter(Boolean);

      return ids.map((instanceId, index) => {
        const snapshot =
          snapshotList.find((entry) => (entry?.instanceId || entry?.id) === instanceId) ||
          snapshotList[index] ||
          {};
        const sourceItem = sourceItems?.[instanceId] || null;
        return normalizeLostItemPreview(instanceId, sourceItem, snapshot);
      });
    };

    const inventoryStore = useInventoryStore();

    const fetchEventData = async () => {
      if (!user.value?.uid || !props.eventId) {
        errorMessage.value = 'ข้อมูลไม่ครบถ้วน';
        loading.value = false;
        return;
      }

      try {
        const eventRef = projectFirestore.collection('events').doc(props.eventId);
        const tattoosRef = projectFirestore.collection('tattoos');

        // Fetch character data
        const charData = playerStore.characterData;
        if (charData) {
          characterData.value = charData;
        } else {
          throw new Error('ไม่พบข้อมูลตัวละคร');
        }

        // Fetch character private data
        const charPrivateData = playerStore.characterPrivateData;
        if (charPrivateData) {
          characterPrivateData.value = charPrivateData;
        } else {
          throw new Error('ไม่พบข้อมูลตัวละครส่วนตัว');
        }

        const bagData = inventoryStore.bagData || {};

        // Fetch event data
        const eventDoc = await eventRef.get();
        if (!eventDoc.exists) {
          throw new Error('ไม่พบข้อมูลการเกิดใหม่');
        }
        eventData.value = eventDoc.data();
        reason.value = eventDoc.data().reason || '';
        skillsToLose.value = [];
        tattoosToLose.value = [];
        itemsToLose.value = [];
        bagItemsToLose.value = [];

        // Fetch lost items if punishment is accepted
        if (eventData.value.punishment) {
          // Skills
          if (eventData.value.skillToLose?.length) {
            skillsToLose.value = eventData.value.skillToLose.map((skillName) => {
              const data = gameItemsData[skillName];
              return {
                name: data?.name || skillName,
                stats: data?.stats || { stamina: 0, str: 0, agi: 0, int: 0 },
                img: data?.img || '../../assets/item/book.png',
              };
            });
          }

          // Tattoos
          if (eventData.value.tattooToLose?.length) {
            const tattooPromises = eventData.value.tattooToLose.map((tattooName) =>
              tattoosRef.doc(tattooName).get().then((tattooDoc) => ({
                id: tattooDoc.id,
                name: tattooDoc.exists ? tattooDoc.data()?.name || tattooName : tattooName,
                stats: tattooDoc.exists ? tattooDoc.data()?.stats || { stamina: 0, str: 0, agi: 0, int: 0 } : { stamina: 0, str: 0, agi: 0, int: 0 },
                imageUrl: tattooDoc.exists ? tattooDoc.data()?.imageUrl || '../../assets/item/tattoo.png' : '../../assets/item/tattoo.png',
              }))
            );
            tattoosToLose.value = await Promise.all(tattooPromises);
          }

          const equippedItems = characterPrivateData.value?.equippedItems || {};
          const bagItemsMap = bagData?.items || {};
          itemsToLose.value = resolveLostItemPreviews(
            eventData.value.itemToLose || [],
            equippedItems,
            eventData.value.itemToLoseSnapshots || []
          );
          bagItemsToLose.value = resolveLostItemPreviews(
            eventData.value.bagItemToLose || [],
            bagItemsMap,
            eventData.value.bagItemToLoseSnapshots || []
          );
        }

        // Fetch killer data
        if (eventData.value.killedBySurvivor && eventData.value.killerId) {
          const killerDoc = await projectFirestore.collection('characters').doc(eventData.value.killerId).get();
          if (killerDoc.exists) {
            killerData.value = killerDoc.data();
          }
        }
      } catch (err) {
        errorMessage.value = err.message || 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    };

    const handleAcceptPunishment = async () => {
      if (loading.value || !user.value || needsMigrate.value) return;
      loading.value = true;
      errorMessage.value = '';

      try {
        const gameActionsFunction = projectFunctions.httpsCallable('gameActions');
        await gameActionsFunction({ action: 'bornActions', subAction: 'accept', eventId: props.eventId });
        await fetchEventData();
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการยอมรับบทลงโทษ';
      } finally {
        loading.value = false;
      }
    };

    const handleBorn = async () => {
      if (loading.value || !user.value || needsMigrate.value) return;
      loading.value = true;
      errorMessage.value = '';

      try {
        const gameActionsFunction = projectFunctions.httpsCallable('gameActions');
        const result = await gameActionsFunction({ action: 'bornActions', subAction: 'reborn', eventId: props.eventId });
        if (!result.data.success) {
          throw new Error(result.data.message || 'การเกิดใหม่ล้มเหลว');
        }
        emit('bornCompleted');
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการเกิดใหม่';
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }

      fetchEventData();

      try {
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId).onSnapshot(
          (doc) => {
            if (doc.exists) {
              eventData.value = doc.data();
              reason.value = doc.data().reason || '';
            }
          },
          (err) => {
            errorMessage.value = 'ไม่สามารถอัปเดตข้อมูลได้: ' + err.message;
          }
        );
      } catch (err) {
        errorMessage.value = 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้: ' + err.message;
      }

      intervalId = setInterval(() => {
        currentTime.value = Date.now();
      }, 1000);
    });

    onUnmounted(() => {
      if (unsubscribeEvent) unsubscribeEvent();
      if (intervalId) clearInterval(intervalId);
    });

    watch(
      () => eventData.value.punishment,
      async (newPunishment, oldPunishment) => {
        if (newPunishment && !oldPunishment) {
          loading.value = true;
          await fetchEventData();
          loading.value = false;
        }
      }
    );

    return {
      reason,
      isLoadingAuth,
      user,
      loading,
      errorMessage,
      eventData,
      characterData,
      skillsToLose,
      tattoosToLose,
      itemsToLose,
      bagItemsToLose,
      killerData,
      activeTab,
      handleAcceptPunishment,
      handleBorn,
      handleImageError,
      remainingTime,
      insuranceRemaining,
      insuranceActive,
      formatRemainingTime,
      needsMigrate,
    };
  },
};
</script>

<style scoped>
/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}
.animate-spin-pulse {
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Scrollbar */
.max-h-64 {
  max-height: 16rem;
}
.overflow-y-auto::-webkit-scrollbar {
  width: 5px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Button States */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
button:not(:disabled):hover {
  transform: translateY(-2px);
}
button:not(:disabled):active {
  transform: scale(0.98);
}

/* Tooltip */
[data-tooltip] {
  position: relative;
}
[data-tooltip]:hover:after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem;
  border-radius: 0.25rem;
  white-space: nowrap;
  font-size: 0.625rem;
  z-index: 20;
  margin-bottom: 0.5rem;
}

/* Responsive Adjustments */
@media (max-width: 640px) {
  .text-2xl {
    font-size: 1.25rem;
  }
  .text-lg {
    font-size: 1rem;
  }
  .text-sm {
    font-size: 0.75rem;
  }
  .text-xs {
    font-size: 0.625rem;
  }
  .p-5 {
    padding: 0.75rem;
  }
  .w-10 {
    width: 2.5rem;
    height: 2.5rem;
  }
  .grid-cols-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
